import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

type NativeBridgePayload = Record<string, unknown> | null;

type NativeBridgeApi = {
  request?: (type: string, payload?: NativeBridgePayload) => Promise<unknown>;
};

type NativeOwnPhotosWindow = Window & {
  ZalamoNativeApp?: NativeBridgeApi;
  ZalamoPublicSessionNativeApp?: NativeBridgeApi;
};

export type NativeOwnPhoto = {
  id: string;
  context: string;
  originalName: string;
  size: number;
  width: number;
  height: number;
  count: number;
  ordered: boolean;
  printFormatId: string | null;
  crop: number[];
  thumbnailDataUrl: string | null;
};

export type NativeOwnPhotoUploadToken = {
  storageUri: string;
  storageAccessToken: string;
  container: string;
  filename: string;
};

export type NativeOwnPhotoUploadProgress = {
  ownPhotoWaitingForUploadId: string | null;
  nativePhotoId: string | null;
  originalName: string | null;
  size: number | null;
  uploadedBytes: number;
  totalBytes: number;
  progress: number;
};

type NativeOwnPhotosResponse = {
  photos: NativeOwnPhoto[];
};

type NativeOwnPhotoThumbnailResponse = {
  id: string;
  thumbnailDataUrl: string | null;
};

@Injectable({
  providedIn: 'root',
})
export class NativeOwnPhotosBridgeService {
  private readonly availableSubject = new BehaviorSubject<boolean>(
    this.isAvailable(),
  );
  readonly available$ = this.availableSubject.asObservable();

  private readonly uploadProgressSubject =
    new Subject<NativeOwnPhotoUploadProgress>();
  readonly uploadProgress$ = this.uploadProgressSubject.asObservable();

  constructor(private readonly ngZone: NgZone) {
    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener('zalamoNativeAppReady', this.handleNativeReady);
    window.addEventListener(
      'zalamoNativeOwnPhotoUploadProgress',
      this.handleNativeUploadProgressEvent as EventListener,
    );
    window.addEventListener(
      'message',
      this.handleNativeUploadProgressWindowMessage as EventListener,
    );
  }

  isAvailable() {
    if (typeof window === 'undefined') {
      return false;
    }

    const nativeWindow = window as NativeOwnPhotosWindow;
    return (
      typeof nativeWindow.ZalamoNativeApp?.request === 'function' ||
      typeof nativeWindow.ZalamoPublicSessionNativeApp?.request === 'function'
    );
  }

  waitForAvailability(timeoutMs: number) {
    if (this.isAvailable()) {
      this.availableSubject.next(true);
      return Promise.resolve(true);
    }

    if (typeof window === 'undefined' || timeoutMs <= 0) {
      return Promise.resolve(false);
    }

    return new Promise<boolean>((resolve) => {
      let resolved = false;

      const finish = (available: boolean) => {
        if (resolved) {
          return;
        }

        resolved = true;
        window.removeEventListener('zalamoNativeAppReady', onReady);
        window.clearTimeout(timeoutId);
        this.availableSubject.next(available);
        resolve(available);
      };

      const onReady = () => finish(this.isAvailable());
      const timeoutId = window.setTimeout(
        () => finish(this.isAvailable()),
        timeoutMs,
      );

      window.addEventListener('zalamoNativeAppReady', onReady);
    });
  }

  async pickOwnPhotos(options: {
    context: string;
    printFormatId: string | null;
    timeoutMs?: number;
  }) {
    const response = await this.request<NativeOwnPhotosResponse>(
      'pickOwnPhotos',
      {
        context: options.context,
        printFormatId: options.printFormatId,
      },
      options.timeoutMs ?? 120000,
    );

    return this.normalizePhotosResponse(response);
  }

  async listOwnPhotos(options: { context: string; timeoutMs?: number }) {
    const response = await this.request<NativeOwnPhotosResponse>(
      'listOwnPhotos',
      {
        context: options.context,
      },
      options.timeoutMs ?? 5000,
    );

    return this.normalizePhotosResponse(response);
  }

  async getOwnPhotoThumbnail(options: {
    context: string;
    id: string;
    timeoutMs?: number;
  }) {
    const response = await this.request<NativeOwnPhotoThumbnailResponse>(
      'getOwnPhotoThumbnail',
      {
        context: options.context,
        id: options.id,
      },
      options.timeoutMs ?? 30000,
    );

    const payload = this.asRecord(response);
    return {
      id: `${payload?.id || options.id}`.trim(),
      thumbnailDataUrl: `${payload?.thumbnailDataUrl || ''}`.trim() || null,
    };
  }

  updateOwnPhoto(options: {
    context: string;
    id: string;
    count: number;
    ordered?: boolean;
    printFormatId: string | null;
    crop: number[];
    timeoutMs?: number;
  }) {
    return this.request(
      'updateOwnPhoto',
      {
        context: options.context,
        id: options.id,
        count: options.count,
        ordered: options.ordered,
        printFormatId: options.printFormatId,
        crop: options.crop,
      },
      options.timeoutMs ?? 5000,
    );
  }

  deleteOwnPhoto(options: { context: string; id: string; timeoutMs?: number }) {
    return this.request(
      'deleteOwnPhoto',
      {
        context: options.context,
        id: options.id,
      },
      options.timeoutMs ?? 10000,
    );
  }

  uploadOwnPhoto(options: {
    context: string | null;
    originalName: string;
    size: number;
    ownPhotoWaitingForUploadId: string;
    uploadToken: NativeOwnPhotoUploadToken;
    timeoutMs?: number;
  }) {
    return this.request(
      'uploadOwnPhoto',
      {
        context: options.context,
        originalName: options.originalName,
        size: options.size,
        ownPhotoWaitingForUploadId: options.ownPhotoWaitingForUploadId,
        uploadToken: options.uploadToken,
      },
      options.timeoutMs ?? 30 * 60 * 1000,
    );
  }

  private async request<TPayload>(
    type: string,
    payload: NativeBridgePayload,
    timeoutMs: number,
  ): Promise<TPayload | null> {
    if (!(await this.waitForAvailability(Math.min(timeoutMs, 5000)))) {
      return null;
    }

    const nativeWindow = window as NativeOwnPhotosWindow;
    const request =
      nativeWindow.ZalamoNativeApp?.request ||
      nativeWindow.ZalamoPublicSessionNativeApp?.request;

    if (!request) {
      return null;
    }

    return new Promise<TPayload | null>((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        resolve(null);
      }, timeoutMs);

      request(type, payload)
        .then((response) => {
          window.clearTimeout(timeoutId);
          resolve((response as TPayload | null) ?? null);
        })
        .catch((error) => {
          window.clearTimeout(timeoutId);
          reject(
            error instanceof Error
              ? error
              : new Error('Native own photos bridge request failed.'),
          );
        });
    });
  }

  private normalizePhotosResponse(
    response: NativeOwnPhotosResponse | null,
  ): NativeOwnPhoto[] {
    const payload = this.asRecord(response);
    const photos = Array.isArray(payload?.photos) ? payload.photos : [];

    return photos
      .map((photo) => this.normalizePhoto(photo))
      .filter((photo): photo is NativeOwnPhoto => photo !== null);
  }

  private normalizePhoto(value: unknown): NativeOwnPhoto | null {
    const photo = this.asRecord(value);
    if (!photo) {
      return null;
    }

    const id = `${photo.id || ''}`.trim();
    const context = `${photo.context || ''}`.trim();
    const originalName = `${photo.originalName || ''}`.trim();
    const size = Number(photo.size || 0);
    const width = Number(photo.width || 0);
    const height = Number(photo.height || 0);

    if (!id || !context || !originalName || !size || !width || !height) {
      return null;
    }

    return {
      id,
      context,
      originalName,
      size,
      width,
      height,
      count: Math.max(1, Number(photo.count || 1)),
      ordered: photo.ordered === true,
      printFormatId: `${photo.printFormatId || ''}`.trim() || null,
      crop: this.normalizeCrop(photo.crop),
      thumbnailDataUrl: `${photo.thumbnailDataUrl || ''}`.trim() || null,
    };
  }

  private normalizeCrop(value: unknown) {
    return Array.isArray(value)
      ? value
          .map((point) => Number(point))
          .filter((point) => Number.isFinite(point))
      : [];
  }

  private readonly handleNativeReady = () => {
    this.ngZone.run(() => {
      this.availableSubject.next(this.isAvailable());
    });
  };

  private readonly handleNativeUploadProgressEvent = (
    event: CustomEvent<NativeOwnPhotoUploadProgress>,
  ) => {
    this.handleUploadProgress(event?.detail);
  };

  private readonly handleNativeUploadProgressWindowMessage = (
    event: MessageEvent<Record<string, unknown>>,
  ) => {
    const data = event?.data;
    if (!data || data.target !== 'zalamo-native-own-photo-upload-progress') {
      return;
    }

    this.handleUploadProgress(data);
  };

  private handleUploadProgress(value: unknown) {
    const payload = this.asRecord(value);
    if (!payload) {
      return;
    }

    const totalBytes = Math.max(0, Number(payload.totalBytes || 0));
    const uploadedBytes = Math.max(0, Number(payload.uploadedBytes || 0));
    const progress = Math.max(0, Math.min(1, Number(payload.progress || 0)));

    this.ngZone.run(() => {
      this.uploadProgressSubject.next({
        ownPhotoWaitingForUploadId:
          `${payload.ownPhotoWaitingForUploadId || ''}`.trim() || null,
        nativePhotoId: `${payload.nativePhotoId || ''}`.trim() || null,
        originalName: `${payload.originalName || ''}`.trim() || null,
        size: payload.size == null ? null : Number(payload.size),
        uploadedBytes,
        totalBytes,
        progress,
      });
    });
  }

  private asRecord(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object'
      ? (value as Record<string, unknown>)
      : null;
  }
}
